import { useEffect, useRef, useState } from 'react';
import GlassCard from './GlassCard';
import Badge from './Badge';

interface Airport {
  code: string;
  name: string;
  city: string;
  lat: number;
  lng: number;
  flight_count?: number;
}

interface Route {
  from: string;
  to: string;
  flight_count: number;
  from_coords: [number, number];
  to_coords: [number, number];
}

interface CrewDestination {
  date: string;
  flight_number: string;
  departure: {
    code: string;
    coordinates: Airport | null;
  };
  arrival: {
    code: string;
    coordinates: Airport | null;
  };
}

interface CrewMapProps {
  crewId?: number;
  crewName?: string;
  destinations?: CrewDestination[];
  airports?: Airport[];
  routes?: Route[];
  highlightAirports?: string[];
  showRoutes?: boolean;
  height?: string;
  onAirportClick?: (airport: Airport) => void;
}

const CrewMap: React.FC<CrewMapProps> = ({
  crewId: _crewId,
  crewName,
  destinations = [],
  airports = [],
  routes = [],
  highlightAirports = [],
  showRoutes = true,
  height = '500px',
  onAirportClick,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [selectedAirport, setSelectedAirport] = useState<Airport | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    // Load Leaflet dynamically
    const loadLeaflet = async () => {
      if (typeof window !== 'undefined' && !window.L) {
        // Load Leaflet CSS
        const linkEl = document.createElement('link');
        linkEl.rel = 'stylesheet';
        linkEl.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(linkEl);

        // Load Leaflet JS
        await new Promise<void>((resolve) => {
          const scriptEl = document.createElement('script');
          scriptEl.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
          scriptEl.onload = () => resolve();
          document.head.appendChild(scriptEl);
        });
      }
      setMapLoaded(true);
    };

    loadLeaflet();
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.L) return;

    // Initialize map if not already done
    if (!mapInstanceRef.current) {
      const L = window.L;

      // Create map centered on Atlantic (between Bermuda and US East Coast)
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [35, -65],
        zoom: 4,
        scrollWheelZoom: true,
        zoomControl: true,
      });

      // Add dark tile layer
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
        subdomains: 'abcd',
        maxZoom: 19,
      }).addTo(mapInstanceRef.current);
    }

    const map = mapInstanceRef.current;
    const L = window.L;

    // Clear existing layers (except base tile layer)
    map.eachLayer((layer: any) => {
      if (layer._url === undefined) {
        map.removeLayer(layer);
      }
    });

    // Create custom icon
    const createIcon = (color: string, size: number = 12) => {
      return L.divIcon({
        className: 'custom-marker',
        html: `<div style="
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.4);
        "></div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    };

    // Determine which airports to show
    const airportsToShow = airports.length > 0 ? airports : [];

    // Add markers for airports
    const bounds: [number, number][] = [];

    airportsToShow.forEach((airport) => {
      const isHighlighted = highlightAirports.includes(airport.code);
      const color = isHighlighted ? '#22c55e' : '#3b82f6';
      const size = isHighlighted ? 16 : 12;

      const marker = L.marker([airport.lat, airport.lng], {
        icon: createIcon(color, size),
      }).addTo(map);

      // Add popup
      marker.bindPopup(`
        <div style="min-width: 150px;">
          <div style="font-weight: bold; font-size: 14px; margin-bottom: 4px;">${airport.code}</div>
          <div style="color: #666; font-size: 12px;">${airport.name}</div>
          <div style="color: #888; font-size: 11px;">${airport.city}</div>
          ${airport.flight_count ? `<div style="margin-top: 8px; color: #3b82f6; font-size: 11px;">${airport.flight_count} flights</div>` : ''}
        </div>
      `);

      marker.on('click', () => {
        setSelectedAirport(airport);
        if (onAirportClick) {
          onAirportClick(airport);
        }
      });

      bounds.push([airport.lat, airport.lng]);
    });

    // Add destinations from crew if provided
    if (destinations.length > 0) {
      destinations.forEach((dest) => {
        if (dest.departure.coordinates) {
          const depCoords = dest.departure.coordinates;
          const marker = L.marker([depCoords.lat, depCoords.lng], {
            icon: createIcon('#22c55e', 14),
          }).addTo(map);

          marker.bindPopup(`
            <div>
              <div style="font-weight: bold;">${dest.departure.code}</div>
              <div style="font-size: 12px; color: #666;">${dest.flight_number}</div>
              <div style="font-size: 11px; color: #888;">${dest.date}</div>
            </div>
          `);

          bounds.push([depCoords.lat, depCoords.lng]);
        }

        if (dest.arrival.coordinates) {
          const arrCoords = dest.arrival.coordinates;
          const marker = L.marker([arrCoords.lat, arrCoords.lng], {
            icon: createIcon('#ef4444', 14),
          }).addTo(map);

          marker.bindPopup(`
            <div>
              <div style="font-weight: bold;">${dest.arrival.code}</div>
              <div style="font-size: 12px; color: #666;">${dest.flight_number}</div>
              <div style="font-size: 11px; color: #888;">${dest.date}</div>
            </div>
          `);

          bounds.push([arrCoords.lat, arrCoords.lng]);

          // Draw route line
          if (dest.departure.coordinates) {
            const depCoords = dest.departure.coordinates;
            L.polyline(
              [
                [depCoords.lat, depCoords.lng],
                [arrCoords.lat, arrCoords.lng],
              ],
              {
                color: '#8b5cf6',
                weight: 2,
                opacity: 0.7,
                dashArray: '5, 10',
              }
            ).addTo(map);
          }
        }
      });
    }

    // Add route lines
    if (showRoutes && routes.length > 0) {
      routes.forEach((route) => {
        const line = L.polyline([route.from_coords, route.to_coords], {
          color: '#3b82f6',
          weight: Math.min(route.flight_count / 10 + 1, 4),
          opacity: 0.5,
        }).addTo(map);

        line.bindPopup(`
          <div>
            <div style="font-weight: bold;">${route.from} → ${route.to}</div>
            <div style="font-size: 12px; color: #666;">${route.flight_count} flights</div>
          </div>
        `);
      });
    }

    // Fit bounds if we have markers
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }

    return () => {
      // Cleanup on unmount
    };
  }, [mapLoaded, airports, routes, destinations, highlightAirports, showRoutes, onAirportClick]);

  return (
    <GlassCard className="relative overflow-hidden">
      {crewName && (
        <div className="absolute top-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur px-4 py-2 rounded-lg">
          <div className="text-sm text-slate-400">Destinations for</div>
          <div className="font-semibold text-white">{crewName}</div>
        </div>
      )}

      {selectedAirport && (
        <div className="absolute top-4 right-4 z-[1000] bg-slate-900/90 backdrop-blur px-4 py-3 rounded-lg min-w-[200px]">
          <div className="flex items-center justify-between mb-2">
            <Badge variant="info">{selectedAirport.code}</Badge>
            <button
              onClick={() => setSelectedAirport(null)}
              className="text-slate-400 hover:text-white"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="text-white font-medium">{selectedAirport.name}</div>
          <div className="text-slate-400 text-sm">{selectedAirport.city}</div>
          {selectedAirport.flight_count !== undefined && (
            <div className="text-blue-400 text-sm mt-2">{selectedAirport.flight_count} scheduled flights</div>
          )}
        </div>
      )}

      <div
        ref={mapRef}
        style={{ height, width: '100%' }}
        className="rounded-lg"
      />

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-[1000] bg-slate-900/90 backdrop-blur px-4 py-3 rounded-lg">
        <div className="text-xs text-slate-400 mb-2">Legend</div>
        <div className="flex flex-col gap-1 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-blue-500" />
            <span className="text-slate-300">Airport</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="text-slate-300">Departure</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="text-slate-300">Arrival</span>
          </div>
          {showRoutes && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-0.5 bg-blue-500" />
              <span className="text-slate-300">Route</span>
            </div>
          )}
        </div>
      </div>
    </GlassCard>
  );
};

// TypeScript declaration for Leaflet
declare global {
  interface Window {
    L: any;
  }
}

export default CrewMap;
